const normalizeBaseUrl = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().replace(/\/+$/, '');
};

const toRelativeSameOriginApiPath = (value) => {
  if (!value || typeof window === 'undefined' || !window.location?.origin) {
    return value;
  }

  try {
    const parsed = new URL(value);
    if (parsed.origin !== window.location.origin) {
      return value;
    }

    return parsed.pathname.replace(/\/+$/, '') || '/api';
  } catch {
    return value;
  }
};

const isLoopbackHost = (host) => {
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
};

const isRemoteAbsoluteApiUrl = (value) => {
  if (!value) {
    return false;
  }

  try {
    const parsed = new URL(value);
    return !isLoopbackHost(parsed.hostname);
  } catch {
    return false;
  }
};

const alignLoopbackProtocolWithPage = (value) => {
  if (!value || typeof window === 'undefined' || !window.location?.protocol) {
    return value;
  }

  try {
    const parsed = new URL(value);
    if (!isLoopbackHost(parsed.hostname)) {
      return value;
    }

    if (window.location.protocol === 'http:' && parsed.protocol === 'https:') {
      parsed.protocol = 'http:';
      return parsed.toString().replace(/\/+$/, '');
    }
  } catch {
    return value;
  }

  return value;
};

const remapLoopbackToCurrentHost = (value) => {
  if (!value || typeof window === 'undefined' || !window.location?.hostname) {
    return value;
  }

  try {
    const parsed = new URL(value);
    if (!isLoopbackHost(parsed.hostname)) {
      return value;
    }

    const currentHost = window.location.hostname;
    if (!currentHost || isLoopbackHost(currentHost)) {
      return value;
    }

    parsed.hostname = currentHost;
    parsed.protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    if (!parsed.port) {
      parsed.port = '8000';
    }

    return parsed.toString().replace(/\/+$/, '');
  } catch {
    return value;
  }
};

const resolveFallbackApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    return `${protocol}//${window.location.hostname}:8080/api`;
  }

  return 'http://127.0.0.1:8080/api';
};

const resolveBrowserApiBaseUrl = () => {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return '';
  }

  if (!isLoopbackHost(window.location.hostname)) {
    return '/api';
  }

  return '';
};

const configuredBaseUrl = normalizeBaseUrl(process.env.REACT_APP_API_URL);
const resolvedConfiguredBaseUrl = alignLoopbackProtocolWithPage(
  remapLoopbackToCurrentHost(configuredBaseUrl)
);
// If an explicit API URL is configured, prefer it (even when the UI is opened
// from a non-loopback host like an iPad on the LAN). The "/api" same-origin
// shortcut is only valid when the frontend server actually proxies that path.
const browserApiBaseUrl = resolvedConfiguredBaseUrl ? '' : resolveBrowserApiBaseUrl();
const allowRemoteApiOnLoopback = String(process.env.REACT_APP_ALLOW_REMOTE_API_ON_LOOPBACK || '').toLowerCase() === 'true';

let API_BASE_URL = browserApiBaseUrl
  || toRelativeSameOriginApiPath(resolvedConfiguredBaseUrl || '')
  || resolvedConfiguredBaseUrl
  || resolveFallbackApiBaseUrl();

// Guardrail: a production build opened on localhost should not silently call production API.
if (
  typeof window !== 'undefined'
  && isLoopbackHost(window.location.hostname)
  && isRemoteAbsoluteApiUrl(API_BASE_URL)
  && !allowRemoteApiOnLoopback
) {
  API_BASE_URL = resolveFallbackApiBaseUrl();
}

export default API_BASE_URL;
